import {
  canonicalizeKonekoUsername,
  resolveKonekoAPlanningUsernameKey,
} from "@/config/konekoAConfig";
import {
  PAYROLL_EMPLOYEE_ROSTER,
  PAYROLL_ROSTER_NAME_ALIASES,
} from "@/config/payrollEmployeeRoster";
import {
  payrollReportRowIncludedForMonth,
} from "@/utils/payrollRosterExclusions";
import {
  displayNameForTimesheetUserId,
  normalizeTimesheetRowUserId,
  resolveTeamRowUserId,
} from "@/utils/timesheetRosterLookup";

function normalizeLookupKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function decimalHoursToHhMm(decimalHours) {
  const h = Number(decimalHours);
  if (!Number.isFinite(h) || h <= 0) return "";
  const totalMinutes = Math.round(h * 60);
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  return `${hh}:${String(mm).padStart(2, "0")}`;
}

function decimalHoursToDecimalString(decimalHours) {
  const h = Number(decimalHours);
  if (!Number.isFinite(h) || h <= 0) return "";
  return (Math.round(h * 100) / 100).toFixed(2);
}

function parseRowHours(row) {
  for (const key of ["hours_with_timer", "working_hours", "hours"]) {
    const raw = row[key];
    if (raw == null || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

/** Build indexes from NekoHR team rows for matching EMP roster lines. */
export function buildPayrollTeamMatchIndex(teamRows) {
  const byFullName = new Map();
  const byUserId = new Map();

  (teamRows || []).forEach((row) => {
    const uid = resolveTeamRowUserId(row);
    if (uid == null) return;
    const fullName = String(row.full_name || row.name || "").trim();
    const uname = canonicalizeKonekoUsername(
      row.username ||
        (row.user && typeof row.user === "object" ? row.user.username : "")
    );
    const entry = {userId: uid, fullName, username: uname};
    byUserId.set(uid, entry);
    if (fullName) {
      byFullName.set(normalizeLookupKey(fullName), entry);
    }
    if (uname) byFullName.set(normalizeLookupKey(uname), entry);
    const planning = resolveKonekoAPlanningUsernameKey(uname || fullName);
    if (planning) byFullName.set(normalizeLookupKey(planning), entry);
  });

  return {byFullName, byUserId};
}

function resolveRosterRowUserId(rosterRow, teamIndex) {
  const candidates = [
    rosterRow.nameInKoneko,
    rosterRow.fullName,
    rosterRow.name,
    rosterRow.empOutId,
  ];
  for (const raw of candidates) {
    const key = normalizeLookupKey(raw);
    if (!key) continue;
    const hit = teamIndex.byFullName.get(key);
    if (hit) return hit.userId;
  }
  const nameKey = normalizeLookupKey(rosterRow.name);
  const alias =
    PAYROLL_ROSTER_NAME_ALIASES[nameKey] ||
    resolveKonekoAPlanningUsernameKey(rosterRow.name) ||
    resolveKonekoAPlanningUsernameKey(rosterRow.nameInKoneko);
  if (alias) {
    const hit = teamIndex.byFullName.get(normalizeLookupKey(alias));
    if (hit) return hit.userId;
  }
  const fromKoneko = resolveKonekoAPlanningUsernameKey(rosterRow.nameInKoneko);
  if (fromKoneko) {
    const hit = teamIndex.byFullName.get(normalizeLookupKey(fromKoneko));
    if (hit) return hit.userId;
  }
  return null;
}

/** Sum hours and line counts per Django user id from normalized timesheet rows. */
export function aggregatePayrollHoursByUserId(timesheetRows) {
  const hoursByUser = {};
  const linesByUser = {};
  (timesheetRows || []).forEach((row) => {
    const uid = normalizeTimesheetRowUserId(row);
    if (uid == null) return;
    const hours = parseRowHours(row);
    if (!Number.isFinite(hours) || hours <= 0) return;
    hoursByUser[uid] = (hoursByUser[uid] || 0) + hours;
    linesByUser[uid] = (linesByUser[uid] || 0) + 1;
  });
  return {hoursByUser, linesByUser};
}

/**
 * Merge EMP roster with month timesheet totals.
 * @param {string} monthKey YYYY-MM
 * @param {object[]} timesheetRows normalized timesheet lines
 * @param {object[]} teamRows team-members rows for name matching (may include timesheet enrich)
 * @param {Record<number, object>} rosterLookup from buildTimesheetRosterLookup
 * @param {object} [options]
 * @param {object[]} [options.apiTeamRows] nekoapi Member rows only (for employment filter)
 * @param {Record<string, object>} [options.rosterMetaByUsername]
 */
export function buildPayrollEmpReportRows(
  monthKey,
  timesheetRows,
  teamRows,
  rosterLookup,
  options = {}
) {
  const apiTeamRows = options.apiTeamRows || teamRows;
  const rosterMetaByUsername = options.rosterMetaByUsername || null;
  const teamIndex = buildPayrollTeamMatchIndex(teamRows);
  const {hoursByUser, linesByUser} = aggregatePayrollHoursByUserId(
    timesheetRows
  );

  return PAYROLL_EMPLOYEE_ROSTER.map((row) => {
    const userId = resolveRosterRowUserId(row, teamIndex);
    const hoursDec = userId != null ? hoursByUser[userId] || 0 : 0;
    const lineCount = userId != null ? linesByUser[userId] || 0 : 0;
    const nekohrName =
      userId != null ? displayNameForTimesheetUserId(rosterLookup, userId) : "";
    return {
      ...row,
      userId,
      nekohrName,
      monthKey,
      hoursDecimal: hoursDec,
      hoursHhMm: decimalHoursToHhMm(hoursDec),
      hoursDecimalLabel: decimalHoursToDecimalString(hoursDec),
      timesheetLineCount: lineCount,
      matchedInNekohr: userId != null,
    };
  }).filter((reportRow) =>
    payrollReportRowIncludedForMonth(
      reportRow,
      apiTeamRows,
      monthKey,
      rosterMetaByUsername
    )
  );
}

function csvEscape(value) {
  const s = value == null ? "" : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvRow(cells) {
  return cells.map(csvEscape).join(",");
}

/**
 * Export EMP roster CSV matching the spreadsheet columns + month totals.
 * @param {string} monthLabel e.g. "April 2026"
 */
export function buildPayrollEmpCsv(reportRows, monthKey, monthLabel) {
  const hoursCol = `${monthLabel || monthKey} Total Hours (HH:MM)`;
  const decCol = `${monthLabel || monthKey} Total Hours (decimal)`;
  const linesCol = `${monthLabel || monthKey} Timesheet Lines`;
  const nekohrCol = "NekoHR matched name";

  const lines = [
    csvRow([
      "Name",
      "Name in Koneko",
      "Contract Type",
      "Status",
      "Emp & Out No.",
      "Full Name",
      "EMP/Out No.",
      "",
      "",
      nekohrCol,
      hoursCol,
      decCol,
      linesCol,
    ]),
  ];

  (reportRows || []).forEach((row) => {
    lines.push(
      csvRow([
        row.name,
        row.nameInKoneko,
        row.contractType,
        row.status,
        row.empOutNo,
        row.fullName,
        row.empOutId,
        row.roleNote,
        row.notes,
        row.nekohrName,
        row.hoursHhMm,
        row.hoursDecimalLabel,
        row.timesheetLineCount || "",
      ])
    );
  });

  return `\uFEFF${lines.join("\n")}\n`;
}

export function payrollEmpExportFilename(monthKey) {
  return `${monthKey} 出勤簿 Employee and Outsourcer - EMP.csv`;
}

export function downloadTextFile(
  filename,
  text,
  mimeType = "text/csv;charset=utf-8"
) {
  const blob = new Blob([text], {type: mimeType});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
